import { clsx } from 'clsx';

export type PlanSortField = 'name' | 'price' | 'family' | 'date';
export type PlanStatusFilter = 'all' | 'active' | 'inactive';
export type PlanPriceRange = 'all' | 'low' | 'mid' | 'high';
export type PlanFamilyRange = 'all' | 'low' | 'mid' | 'high';

export const PLAN_TYPE_CONFIG: Record<string, { label: string; className: string }> = {
  funeral: { label: 'Funeral', className: 'bg-primary-50 text-primary-700 ring-1 ring-primary-200/80' },
  cremation: { label: 'Cremación', className: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200/80' },
  burial: { label: 'Entierro', className: 'bg-stone-100 text-stone-700 ring-1 ring-stone-200/80' },
  memorial: { label: 'Memorial', className: 'bg-sky-50 text-sky-700 ring-1 ring-sky-200/80' },
  default: { label: 'Otro', className: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200/80' },
};

export function planTypeLabel(planType: string): string {
  return (PLAN_TYPE_CONFIG[planType] || PLAN_TYPE_CONFIG.default).label;
}

export function planTypeChipClass(planType: string): string {
  return (PLAN_TYPE_CONFIG[planType] || PLAN_TYPE_CONFIG.default).className;
}

export function planInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
}

export function avatarTone(seed: string): string {
  const tones = [
    'bg-primary-100 text-primary-800 ring-primary-200/80',
    'bg-violet-100 text-violet-800 ring-violet-200/80',
    'bg-teal-100 text-teal-900 ring-teal-200/80',
    'bg-amber-100 text-amber-900 ring-amber-200/80',
    'bg-sky-100 text-sky-900 ring-sky-200/80',
  ];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = seed.charCodeAt(i) + ((h << 5) - h);
  return tones[Math.abs(h) % tones.length];
}

export function formatPlanPrice(value: number): string {
  return `$${Number(value || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function matchesPriceRange(price: number, range: PlanPriceRange): boolean {
  switch (range) {
    case 'all':
      return true;
    case 'low':
      return price < 300;
    case 'mid':
      return price >= 300 && price <= 600;
    case 'high':
      return price > 600;
    default: {
      const _exhaustive: never = range;
      return _exhaustive;
    }
  }
}

export function matchesFamilyRange(max: number, range: PlanFamilyRange): boolean {
  switch (range) {
    case 'all':
      return true;
    case 'low':
      return max >= 1 && max <= 4;
    case 'mid':
      return max >= 5 && max <= 8;
    case 'high':
      return max >= 9;
    default: {
      const _exhaustive: never = range;
      return _exhaustive;
    }
  }
}

export function planActiveChipClass(isActive: boolean): string {
  return clsx(
    'inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1',
    isActive
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : 'bg-slate-100 text-slate-600 ring-slate-200'
  );
}
